import { useState, useEffect } from "react";

// ── API CONFIGURATION ─────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "";

function apiUrl(path) {
  const rawBase = String(API_BASE || "").trim();
  const base = rawBase.replace(/\/+$/, "");
  const p = String(path || "");
  const withLeadingSlash = p.startsWith("/") ? p : `/${p}`;

  if (!base) return withLeadingSlash;

  if (/\/api$/i.test(base) && /^\/api(\/|$)/i.test(withLeadingSlash)) {
    return `${base}${withLeadingSlash.replace(/^\/api/i, "")}`;
  }

  return `${base}${withLeadingSlash}`;
}

// ── THEME (Simple, Mobile-Friendly) ──────────────────────────────────────────
const T = {
  bg: "#f8fafc",
  card: "#ffffff",
  border: "#e2e8f0",
  text: "#0f172a",
  text2: "#64748b",
  muted: "#94a3b8",
  success: "#16a34a",
  successBg: "#f0fdf4",
  successBorder: "#86efac",
  danger: "#dc2626",
  dangerBg: "#fef2f2",
  dangerBorder: "#fca5a5",
  primary: "#b91c1c",
  primaryBg: "#fef2f2",
  info: "#2563eb",
  infoBg: "#eff6ff",
  infoBorder: "#93c5fd",
};

// ── UTILITY FUNCTIONS ─────────────────────────────────────────────────────────
function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const mins = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${mins}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function extractTripNumber(fullTripNumber) {
  if (!fullTripNumber) return "";
  const parts = String(fullTripNumber).split('-');
  return parts.length > 1 ? parts[parts.length - 1] : String(fullTripNumber);
}

function parseRoute(fullRoute) {
  if (!fullRoute) return { from: "", to: "" };

  const lines = String(fullRoute).split(/\r?\n/).filter(l => l.trim());
  let from = "";
  let to = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith("From:")) {
      from = line.replace("From:", "").trim();
    } else if (line.startsWith("To:")) {
      to = line.replace("To:", "").trim();
    }
  }

  return { from, to };
}

function JobCard({ job, isMultiple }) {
  const route = parseRoute(job.fullRoute);
  const tripNum = extractTripNumber(job.tripNumber);

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: isMultiple ? "16px" : "20px",
      marginBottom: isMultiple ? 12 : 0,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12,
        paddingBottom: 12,
        borderBottom: `1px solid ${T.border}`,
        flexWrap: "wrap"
      }}>
        <div style={{
          fontSize: isMultiple ? 16 : 18,
          fontWeight: 700,
          color: T.text,
          fontFamily: "monospace"
        }}>
          Trip #{tripNum}
        </div>
        <div style={{ fontSize: 11, color: T.muted }}>•</div>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.info,
          background: T.infoBg,
          border: `1px solid ${T.infoBorder}`,
          padding: "3px 8px",
          borderRadius: 4
        }}>
          {job.serviceTypeName}
        </div>
        {job.customerName && (
          <>
            <div style={{ fontSize: 11, color: T.muted }}>•</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text2 }}>
              {job.customerName}
            </div>
          </>
        )}
      </div>

      <div style={{
        fontSize: 12,
        color: T.text2,
        marginBottom: 12,
        fontWeight: 500
      }}>
        {[
          job.reportDateTime && `${formatTime(job.reportDateTime)} Report`,
          job.spotDateTime && `${formatTime(job.spotDateTime)} Spot`,
          job.pickupDateTime && `${formatTime(job.pickupDateTime)} Start`,
          job.dropOffDateTime && `${formatTime(job.dropOffDateTime)} End`
        ].filter(Boolean).join("  •  ")}
      </div>

      <div style={{
        background: T.bg,
        borderRadius: 8,
        padding: 12,
        marginBottom: 12
      }}>
        {route.from && (
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>FROM: </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{route.from}</span>
          </div>
        )}
        {route.to && (
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>TO: </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{route.to}</span>
          </div>
        )}
      </div>

      {job.paxCount > 0 && (
        <div style={{
          fontSize: 12,
          color: T.text2,
          marginBottom: job.notes || job.specialInstructions ? 12 : 0
        }}>
          👥 <strong>{job.paxCount}</strong> passenger{job.paxCount !== 1 ? "s" : ""}
        </div>
      )}

      {job.notes && (
        <div style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 6,
          padding: 10,
          marginBottom: job.specialInstructions ? 8 : 0,
          fontSize: 12,
          color: "#0c4a6e",
          lineHeight: 1.5
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>📝 Notes:</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{job.notes}</div>
        </div>
      )}

      {job.specialInstructions && (
        <div style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          borderRadius: 6,
          padding: 10,
          fontSize: 12,
          color: "#9a3412",
          lineHeight: 1.5
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>📋 Trip Instructions:</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{job.specialInstructions}</div>
        </div>
      )}
    </div>
  );
}

export default function DriverView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [error, setError] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const linkToken = params.get('token') || window.location.pathname.split('/').pop();

  useEffect(() => {
    loadJobs();
  }, []);

  async function loadJobs() {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/api/driver-view/${linkToken}`));
      if (!response.ok) {
        throw new Error('Failed to load assignment details');
      }
      const data = await response.json();
      const jobsArray = Array.isArray(data) ? data : [data];
      setJobs(jobsArray);

      if (jobsArray.some(j => j.currentStatus === 'confirmed')) {
        setConfirmed(true);
      }
      if (jobsArray.some(j => j.currentStatus === 'declined')) {
        setDeclined(true);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setConfirming(true);
    try {
      const response = await fetch(apiUrl(`/api/driver-view/${linkToken}/confirm`), { method: 'POST' });
      if (!response.ok) {
        throw new Error('Failed to confirm');
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to confirm');
      }
      setConfirmed(true);
      await loadJobs();
    } catch (_err) {
      alert('Failed to confirm. Please try again or call dispatch.');
    } finally {
      setConfirming(false);
    }
  }

  async function handleDecline() {
    if (!window.confirm('Are you sure you want to decline this assignment?')) {
      return;
    }

    setDeclining(true);
    try {
      const response = await fetch(apiUrl(`/api/driver-view/${linkToken}/decline`), { method: 'POST' });
      if (!response.ok) {
        throw new Error('Failed to decline');
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to decline');
      }
      setDeclined(true);
      await loadJobs();
    } catch (_err) {
      alert('Failed to decline. Please call dispatch.');
    } finally {
      setDeclining(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: T.bg, padding: 20 }}>
        <div style={{ fontSize: 14, color: T.muted }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, padding: 20 }}>
        <div style={{ maxWidth: 500, margin: "0 auto", background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, borderRadius: 12, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.danger, marginBottom: 8 }}>
            Unable to Load Assignment
          </div>
          <div style={{ fontSize: 14, color: T.text2, marginBottom: 16 }}>
            {error}
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            Please contact dispatch if you need assistance.
          </div>
        </div>
      </div>
    );
  }

  const isMultiple = jobs.length > 1;
  const firstJob = jobs[0];

  return (
    <div style={{ minHeight: "100vh", background: T.bg, padding: "20px 16px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 600, margin: "0 auto 20px", textAlign: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 4 }}>
          Empire Coach Line
        </div>
        <div style={{ fontSize: 13, color: T.muted }}>
          Driver Confirmation
        </div>
        {firstJob && (
          <div style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>
            {formatDate(firstJob.serviceDate)}
          </div>
        )}
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        {isMultiple && (
          <div style={{ background: T.infoBg, border: `1px solid ${T.infoBorder}`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: T.info, fontWeight: 600, textAlign: "center" }}>
            You have {jobs.length} assignments for this date
          </div>
        )}

        {jobs.map((job, idx) => (
          <JobCard key={idx} job={job} isMultiple={isMultiple} />
        ))}
      </div>

      {!confirmed && !declined && (
        <div style={{ maxWidth: 600, margin: "24px auto 0", display: "flex", gap: 12, flexDirection: "column" }}>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            style={{
              background: confirmed ? T.successBg : "linear-gradient(135deg, #16a34a, #15803d)",
              color: confirmed ? T.success : "#fff",
              border: confirmed ? `2px solid ${T.successBorder}` : "none",
              padding: "16px 24px",
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 700,
              cursor: confirming ? "not-allowed" : "pointer",
              opacity: confirming ? 0.6 : 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}
          >
            {confirming ? "Confirming..." : confirmed ? "✓ Confirmed" : "✓ Confirm Assignment"}
          </button>

          <button
            onClick={handleDecline}
            disabled={declining}
            style={{
              background: T.card,
              color: T.danger,
              border: `2px solid ${T.dangerBorder}`,
              padding: "14px 24px",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: declining ? "not-allowed" : "pointer",
              opacity: declining ? 0.6 : 1
            }}
          >
            {declining ? "Processing..." : "✗ Decline"}
          </button>
        </div>
      )}

      {confirmed && (
        <div style={{ maxWidth: 600, margin: "24px auto 0", background: T.successBg, border: `2px solid ${T.successBorder}`, borderRadius: 12, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.success, marginBottom: 8 }}>
            ✓ Confirmed!
          </div>
          <div style={{ fontSize: 14, color: T.text2 }}>
            Thank you for confirming. See you on the {formatDate(firstJob?.serviceDate).split(',')[0]}!
          </div>
        </div>
      )}

      {declined && (
        <div style={{ maxWidth: 600, margin: "24px auto 0", background: T.dangerBg, border: `2px solid ${T.dangerBorder}`, borderRadius: 12, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.danger, marginBottom: 8 }}>
            Declined
          </div>
          <div style={{ fontSize: 14, color: T.text2 }}>
            Dispatch has been notified. They will contact you shortly.
          </div>
        </div>
      )}

      <div style={{ maxWidth: 600, margin: "32px auto 0", padding: "16px", textAlign: "center", fontSize: 12, color: T.muted }}>
        If you have questions, please contact dispatch.
      </div>
    </div>
  );
}
